import { useEffectOnce } from 'react-use';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import type { GraphQLErrors } from '@apollo/client/errors';
import { FormProvider, useForm } from 'react-hook-form';
import { logError } from '@zg-rentals/log-error';
import { useCappsLeadWebApiHandler_sendLeadOnApplicationSubmit } from '@zg-rentals/ts-capps-client-react-query';
import {
  Banner,
  Button,
  ButtonGroup,
  DialogBody,
  Divider,
  Flex,
  Form,
  Heading,
  Page,
  PageContent,
  Toast,
  useToast,
} from '@zillow/constellation';
import { SaveAnswersDocument, SubmitApplicationDocument } from '../../graphql/queries';
import type { Answers, AnswersInput, UserStatus } from '../../graphql/queries';
import { ApplicationEdit } from '../../components/ApplicationEdit';
import { ApplicationOverview } from '../../components/ApplicationOverview';
import {
  FIELD_IDS,
  getNextStep,
  getPreviousStep,
  getSections,
  getSteps,
  REVIEW_STATUS,
  SECTION_IDS,
  type SectionId,
  STEP_IDS,
} from '../../constants/wizard';
import { getIsReadOnly } from '../../utils/business-logic';
import { FullHeightLoadingMask, ModalWithFullHeightBody } from '../../styles/styled-components';
import { getAnswersInput, getAnswersInputWithoutInvalid, getErrorsWithoutRequired } from '../../utils/application-edit';
import { getApplicationRoute, getEditRoute } from '../../constants/urls';
import { getOverviewFromApplication } from '../../utils/overview/overview-statuses';
import { getIsIdvStepComplete, getIsPaymentStepComplete } from '../../utils/user-statuses';
import { ContactProperty } from '../ContactProperty';
import { SubmitModal } from '../ApplicationEdit/SubmitModal';
import type { ApplicationContextValue } from '../../contexts/ApplicationContext';
import { useApplicationContext } from '../../contexts/ApplicationContext';
import { SUBMISSION_TYPES, useBaseContext } from '../../contexts/BaseContext';
import {
  CLICKSTREAM_CATEGORY_EVENTS,
  emitNextClickEvent,
  emitNextPaymentRequestEvent,
  emitNextScreeningReportsEvent,
  emitOverviewSubmitEvent,
  emitRentalApplicationCompletedEvent,
  emitSubmitScreeningEvent,
  emitViewContentEvent,
  emitViewStepEvent,
} from '../../analytics/clickstreamEvents';
import { ReportModalLoader } from '../Reports';
import { useFormRequiredWhitespace } from '../../utils/useFormRequiredWhitespace';
import { redactObject } from '../../utils/logging';
import { APPLICATION_NOT_SAVED_TEXT } from '../../constants/applicationsConstants';
import { omit } from 'lodash-es';
import { useOptInOutRenterRequestPayment } from '../../utils/rentPaymentQueries';

// TODO - can stepId be from router as well instead of passed in here?
export function ApplicationFullPage({ stepId }: { stepId?: string }) {
  const router = useRouter();
  const applicationContext = useApplicationContext();
  const { application, userStatuses } = applicationContext;
  const { brownoutWarning, setSubmittedApplication } = useBaseContext();
  const { reportType } = router.query as { reportType?: string };
  const applicationId = application.id as string;
  const steps = getSteps(applicationContext.featureFlags);
  const step = steps.find(({ id }) => id === stepId);
  const previousStep = getPreviousStep(stepId, applicationContext.featureFlags);
  const isReadOnly = getIsReadOnly(application);
  const [givesPermission, setGivesPermission] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);

  const initialAnswers = application?.answers ? application.answers : {};
  const form = useFormRequiredWhitespace(useForm({ defaultValues: initialAnswers, mode: 'onBlur' }));
  const [saveAnswersMutation, { loading: savingAnswers }] = useMutation(SaveAnswersDocument);
  const [submitMutation, { loading: submitting }] = useMutation(SubmitApplicationDocument);
  const sendLeadMutation = useCappsLeadWebApiHandler_sendLeadOnApplicationSubmit();
  const { enqueueToast } = useToast();
  const optInOutForZillowPayments = useOptInOutRenterRequestPayment(
    applicationId,
    application.rentalListing?.listingAlias,
  );

  useEffectOnce(() => {
    emitViewContentEvent(applicationContext);
  });

  useEffect(() => {
    emitViewStepEvent(applicationContext, stepId);
  }, [stepId, applicationContext]);

  const requestZillowPayments = form.watch(FIELD_IDS.REQUEST_ZILLOW_PAYMENTS as keyof Answers) as boolean | undefined;

  useEffect(() => {
    // Whenever the route changes, reset the modal body scroll position to the top
    // Otherwise we are stuck looking at the bottom of the new page, if we had been scrolled down previously
    document.querySelector('[data-scroll-top="application-dialog-body"]')?.scroll({ top: 0 });
  }, [router.asPath]);

  /**
   * Calls the saveAnswers mutation and opts in/out of zillow payments
   * Doesn't compare answersToSave against initialAnswers
   * On sucess returns true without enqueuing a toast
   *
   * On failure returns false and sets form errors, if there are no form errors, enqueues a toast to inform
   * the user of the failure
   * @param answersToSave
   * @param shouldResetFormOnSuccess Call form.reset() with saveAnswers response on success
   * @returns true on success, false otherwise
   */
  async function persistChangedAnswers(
    answersToSave: AnswersInput & { requestZillowPayments?: boolean | null },
    shouldResetFormOnSuccess: boolean,
  ) {
    if (Object.keys(answersToSave).length > 0) {
      const variables = {
        answers: omit(answersToSave, 'requestZillowPayments'),
        applicationId,
      };

      try {
        // Only call payment API if the value has actually changed
        if (answersToSave.requestZillowPayments != null) {
          const currentPaymentValue = initialAnswers?.requestZillowPayments;
          const newPaymentValue = !!answersToSave.requestZillowPayments;

          // Only make API call if value has changed
          if (currentPaymentValue !== newPaymentValue) {
            await optInOutForZillowPayments(newPaymentValue);
          }
        }

        const response = await saveAnswersMutation({
          variables,
        });

        if (response.data?.saveAnswers && shouldResetFormOnSuccess) {
          form.reset({
            ...response.data?.saveAnswers,
            requestZillowPayments: answersToSave.requestZillowPayments,
          });
        }
      } catch (err) {
        logError({
          error: err,
          context: {
            details: 'ApplicationFullPage - saveAnswersMutation error',
            variables: redactObject(variables),
          },
        });
        const graphQLErrors: GraphQLErrors = err.graphQLErrors ?? [];

        /** The 'documents' field errors is not handled by react-hook-form
         * so we need to ignore it so the toast error shows up instead
         */
        const ignoreFieldErrors = ['documents'];
        const allInvalidFields = graphQLErrors
          .flatMap((x) => (x?.extensions?.invalidFields ?? []) as Array<keyof Answers>)
          .filter((x) => !ignoreFieldErrors.includes(x));
        if (allInvalidFields.length > 0) {
          for (const invalidField of allInvalidFields) {
            form.setError(invalidField, { type: 'server', message: 'Enter a valid value' });
          }
        } else {
          // We should never have an error condition without user feedback, in this case we show a generic toast
          // just to not have the button do nothing
          enqueueToast(<Toast appearance="warning" body={APPLICATION_NOT_SAVED_TEXT} />);
        }

        return false;
      }
    }
    return true;
  }

  async function persistAnswers(showSuccessToast: boolean) {
    const errors = getErrorsWithoutRequired(form.formState.errors);
    const formValues = form.getValues();
    const answers = getAnswersInput(formValues, initialAnswers);
    const answersToSave = getAnswersInputWithoutInvalid(answers, initialAnswers, errors);

    if (!(await persistChangedAnswers(answersToSave, true))) {
      return false;
    }

    if (showSuccessToast) {
      if (Object.keys(errors).length > 0) {
        enqueueToast(<Toast appearance="info" body="All valid data has been saved" />);
      } else {
        enqueueToast(<Toast appearance="success" body="Your application has been saved" />);
      }
    }

    return true;
  }

  async function onClose() {
    if (!isReadOnly) {
      await form.trigger();
      await persistAnswers(true);
    }
    router.push('/');
  }

  async function onStepPersist() {
    await persistAnswers(false);
  }

  async function onClickBack() {
    await form.trigger();
    const success = await persistAnswers(false);
    if (previousStep && success) {
      router.push(getEditRoute({ applicationId, stepId: previousStep.id }));
    }
  }

  async function onSectionClick(sectionId: SectionId) {
    CLICKSTREAM_CATEGORY_EVENTS[sectionId]?.(applicationContext);

    await form.trigger();
    await persistAnswers(false);
    if (sectionId === SECTION_IDS.REVIEW) {
      router.push(getApplicationRoute({ applicationId }));
    } else {
      const SECTIONS = getSections(applicationContext.featureFlags);
      const section = SECTIONS.find((s) => s.id === sectionId);
      const firstStepInSection = section?.steps[0];
      if (firstStepInSection) {
        router.push(getEditRoute({ applicationId, stepId: firstStepInSection.id }));
      }
    }
  }

  async function onClickNext(formData: Answers) {
    if (!getIsNextDisabled({ applicationContext, stepId, requestZillowPayments })) {
      if (step) {
        if (step.id === STEP_IDS.PAYMENT_AND_REPORTS) {
          emitNextScreeningReportsEvent(applicationContext);
          emitSubmitScreeningEvent(applicationContext);
        } else if (step.id === STEP_IDS.PAYMENT_REQUEST) {
          emitNextPaymentRequestEvent(applicationContext);
        }
        const isSuccess = await onSubmit(formData);
        if (isSuccess) {
          const { nextStepId } = router.query as { nextStepId?: string };
          const nextStep = getNextStep(step.id, applicationContext.featureFlags);
          if (nextStepId) {
            router.push(getEditRoute({ applicationId, stepId: nextStepId }));
          } else if (nextStep) {
            router.push(getEditRoute({ applicationId, stepId: nextStep.id }));
          } else {
            router.push(getApplicationRoute({ applicationId }));
          }
        }
      } else {
        if (givesPermission) {
          emitRentalApplicationCompletedEvent(applicationContext);
          emitOverviewSubmitEvent(applicationContext);
          submitApplication();
        } else {
          setIsSubmitModalOpen(true);
        }
      }
      emitNextClickEvent(applicationContext, stepId);
    }
  }

  async function submitApplication() {
    try {
      const listingAlias = application.rentalListing?.listingAlias;
      await submitMutation({
        variables: {
          applicationId,
        },
      });
      if (listingAlias) {
        sendLeadMutation.mutate({ postData: { applicationId, listingAlias } });
      }
      setSubmittedApplication?.({
        type: SUBMISSION_TYPES.SUBMITTED,
        applicationId,
        listingAlias,
      });
      router.push('/');
    } catch (err) {
      logError({ error: err, context: 'ApplicationFullPage - submitMutation error' });
      enqueueToast(
        <Toast appearance="warning" body="An error occurred. Your application was not submitted. Try again." />,
      );
    }
  }

  async function onSubmit(formData: Answers) {
    const answersToSubmit = getAnswersInput(formData, initialAnswers);
    return await persistChangedAnswers(answersToSubmit, false);
  }

  const loading = savingAnswers || submitting;
  const formSubmitHandler = form.handleSubmit(onClickNext);

  return (
    <>
      <ModalWithFullHeightBody
        isOpen
        fullScreen
        shouldCloseOnOutsideClick={false}
        renderHeader={(headerProps: object) => (
          <>
            {brownoutWarning && <Banner appearance="warning" body={brownoutWarning} />}
            <header {...headerProps}>
              <Flex
                display="flex"
                marginY="sm"
                marginX={{ default: 'xl', md_lte: 'sm' }}
                justifyContent="space-between"
                alignItems="center"
              >
                <Flex
                  display="flex"
                  justifyContent="space-between"
                  flexDirection={{ default: 'row', md_lte: 'column' }}
                  alignItems="start"
                  flexGrow={1}
                >
                  <Heading level={5} as="h1">
                    Rental application
                  </Heading>
                  {application.rentalListing && <ContactProperty application={application} />}
                </Flex>
                <Button buttonType="tertiary" disabled={loading} onClick={onClose} marginLeft="md" type="button">
                  {isReadOnly ? 'Exit' : 'Save & exit'}
                </Button>
              </Flex>
              <Divider />
            </header>
          </>
        )}
        renderBody={(bodyProps) => (
          <DialogBody data-scroll-top="application-dialog-body" {...bodyProps}>
            <FullHeightLoadingMask loading={loading}>
              <Page backgroundColor="white">
                <PageContent
                  marginTop={0}
                  style={{
                    overflowWrap: 'break-word',
                  }}
                >
                  {!isReadOnly && (
                    <FormProvider {...form}>
                      <Form onSubmit={formSubmitHandler}>
                        <ApplicationEdit
                          application={application}
                          userStatuses={userStatuses as Array<UserStatus>}
                          onSectionClick={onSectionClick}
                          step={step}
                          givesPermission={givesPermission}
                          setGivesPermission={setGivesPermission}
                          onPersist={onStepPersist}
                        />
                      </Form>
                    </FormProvider>
                  )}
                  {isReadOnly && <ApplicationOverview />}
                </PageContent>
              </Page>
            </FullHeightLoadingMask>
          </DialogBody>
        )}
        renderFooter={(footerProps: object) =>
          !isReadOnly && (
            <footer {...footerProps}>
              <Divider />
              <Flex
                as={ButtonGroup}
                display="flex"
                justifyContent={previousStep ? 'space-between' : 'flex-end'}
                aria-label="Wizard actions"
                paddingY="sm"
                marginX={{ default: 'xl', md_lte: 'sm' }}
              >
                {previousStep && (
                  <Button onClick={onClickBack} disabled={loading} type="button">
                    Back
                  </Button>
                )}
                <Button
                  onClick={formSubmitHandler}
                  buttonType="primary"
                  disabled={
                    loading ||
                    getIsNextDisabled({
                      applicationContext,
                      stepId: step?.id,
                      requestZillowPayments,
                    })
                  }
                >
                  {step ? 'Next' : 'Submit'}
                </Button>
              </Flex>
            </footer>
          )
        }
        renderCloseButton={null}
      />
      {!isReadOnly && isSubmitModalOpen && (
        <SubmitModal
          givesPermission={givesPermission}
          setGivesPermission={setGivesPermission}
          onClose={() => setIsSubmitModalOpen(false)}
          onClickSubmit={() => {
            setIsSubmitModalOpen(false);
            formSubmitHandler();
          }}
          loading={loading}
        />
      )}
      {reportType && <ReportModalLoader userStatuses={userStatuses} reportType={reportType} />}
    </>
  );
}

function getIsNextDisabled({
  applicationContext,
  stepId,
  requestZillowPayments,
}: {
  applicationContext: ApplicationContextValue;
  stepId?: string;
  requestZillowPayments: boolean | undefined;
}) {
  if (!stepId) {
    const { status } = getOverviewFromApplication(applicationContext, requestZillowPayments);

    return status !== REVIEW_STATUS.COMPLETE;
  }
  const { userStatuses } = applicationContext;
  if (stepId === STEP_IDS.PAYMENT_AND_REPORTS) {
    return !getIsPaymentStepComplete(userStatuses);
  }
  if (stepId === STEP_IDS.ID_VERIFICATION) {
    return !getIsIdvStepComplete(userStatuses);
  }
  return false;
}
